import {
  Alert,
  Tooltip,
  Space,
  Button,
  message,
} from 'antd';
import {
  BulbOutlined,
  SaveOutlined,
  ReadOutlined,
  BulbFilled,
  ExperimentOutlined,
  ExperimentFilled,
  FireOutlined,
} from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { QAItem, QAUpdateProp } from './productQA-item';
import pubSub from '../pubSub';
import { Access, useAccess } from 'umi';
import { FindSimilarityCurrentPQ } from './find-similarity';
import { PredictionData } from '../index';
import { ResultModal } from '../hooks/useResultModal';
import editPubSub from './EditComponent/editPubSub';

const Index: React.FC<{
  QA: QAItem;
  handleSave: (payload: QAUpdateProp) => void;
  validatePredict: boolean;
  setFindSimilarityCurrentPQ: (item: FindSimilarityCurrentPQ) => void;
  predictiveOutput: (data: PredictionData, type: 'predict' | 'verify') => void;
  setCallbackResult: (data: ResultModal) => void;
  handlePrompt: (ids: number[]) => void;
}> = ({
  QA,
  handleSave,
  validatePredict,
  setFindSimilarityCurrentPQ,
  predictiveOutput,
  setCallbackResult,
  handlePrompt,
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [predictionLoading, setPredictionLoading] = useState<boolean>(false);
    const [predictionText, setPredictionText] = useState<string>('');

    const [preAuditLoading, setPreAuditLoading] = useState<boolean>(false);
    const [preAudit, setPreAudit] = useState<string>('');
    const [isPreAudit, setIsPreAudit] = useState<boolean>(false);

    const access = useAccess();

    useEffect(() => {
      if (QA.id) {
        if (QA.preAuditComment) setPreAudit(QA.preAuditComment);
        setIsPreAudit(QA.isPreAudit);
        pubSub.subscribeJobResult(QA.id, callback);
      }
    }, []);

    const callback = (data: {
      content: string;
      type: 'preAudit' | 'prediction';
      isPreAudit?: boolean;
    }) => {
      if (data.type === 'prediction') {
        setPredictionText(data.content);
        setPredictionLoading(false);
      } else if (data.type === 'preAudit') {
        setPreAudit(data.content);
        setIsPreAudit(Boolean(data.isPreAudit));
        setPreAuditLoading(false);
      }
    };

    const handleQASave = async () => {
      if (loading) return;

      const { question, answer, remark, trigger } = editPubSub.get(QA.id, QA);
      if (question + answer + (remark || "") + (trigger || "") === (QA.question + QA.answer + (QA.remark || "") + (QA.trigger || ""))) {
        return message.info('没有修改');
      }

      if (!question || !answer) return message.info('内容不能为空');
      let data: { answer?: string; question?: string; remark?: string | null; trigger?: string | null } = {};
      if (question !== QA.question) {
        data['question'] = (question as string).trim();
      }
      if (answer !== QA.answer) {
        data['answer'] = (answer as string).trim();
      }
      data['remark'] = String(remark || "").trim() || null;
      data['trigger'] = String(trigger || "").trim() || null;
      setLoading(true);
      await handleSave({
        id: QA.id,
        data,
      });
      setLoading(false);
    };

    const predict = (type: 'predict' | 'verify' = 'predict') => {
      const { question, answer } = editPubSub.get(QA.id, QA);
      type === 'predict' ? setPredictionLoading(true) : setPreAuditLoading(true);
      predictiveOutput(
        [
          {
            rowId: QA.id,
            question,
            answer,
            product: QA.productName,
          },
        ],
        type,
      );
    };

    const iconButtonStyle = { padding: 0, margin: 0, fontSize: 20 } as React.CSSProperties;

    return <Space size={10} style={{ marginLeft: '10px', maxWidth: 100 }} wrap>
      <Access accessible={access.productQAEdit && QA.permit}>
        <Tooltip title="机器人预复核">
          <Button
            style={{ ...iconButtonStyle, color: preAudit ? 'orange' : '' }}
            type="text"
            loading={preAuditLoading}
            onClick={() => {
              if (preAudit) {
                setCallbackResult({
                  visible: true,
                  text: (
                    <Alert
                      message={`机器人预复核-${isPreAudit ? '通过' : '未通过'
                        }`}
                      description={preAudit}
                      style={{ width: '100%', whiteSpace: 'pre-wrap' }}
                      type={isPreAudit ? 'success' : 'error'}
                    />
                  ),
                  footer: (
                    <Button
                      type="primary"
                      onClick={() => {
                        setCallbackResult({ visible: false, text: <></> });
                        predict('verify');
                      }}
                    >
                      重新预复核
                    </Button>
                  ),
                });
                return;
              }
              predict('verify');
            }}
          >
            {preAudit ? <ExperimentFilled /> : <ExperimentOutlined />}
          </Button>
        </Tooltip>
      </Access>
      <Access accessible={validatePredict}>
        <Tooltip title="预测输出">
          <Button
            style={{
              ...iconButtonStyle,
              color: predictionText ? 'orange' : '',
            }}
            type="text"
            loading={predictionLoading}
            onClick={() => {
              if (predictionText) {
                setCallbackResult({
                  visible: true,
                  text: (
                    <Alert
                      message={'预测输出'}
                      description={predictionText}
                      style={{ width: '100%', whiteSpace: 'pre-wrap' }}
                      type="success"
                    />
                  ),
                  footer: (
                    <Button
                      type="primary"
                      onClick={() => {
                        setCallbackResult({ visible: false, text: <></> });
                        predict();
                      }}
                    >
                      重新预测
                    </Button>
                  ),
                });
                return;
              }
              predict();
            }}
          >
            {predictionText ? <BulbFilled /> : <BulbOutlined />}
          </Button>
        </Tooltip>
      </Access>
      <Tooltip title="找相似问题及答案">
        <Button
          style={iconButtonStyle}
          type="text"
          onClick={() =>
            setFindSimilarityCurrentPQ({
              id: QA.id,
              productName: QA.productName,
              editForm: (question: string, answer: string) => {
                editPubSub.publish(QA.id, "question", question);
                editPubSub.publish(QA.id, "answer", answer);
              },
              question: QA.question,
            })
          }
        >
          <ReadOutlined />
        </Button>
      </Tooltip>
      <Access accessible={access.productQAEdit && QA.permit && QA.verifiedStatus !== 'STORED'}>
        <Tooltip title="利用提示词生成回答">
          <Button
            style={iconButtonStyle}
            type="text"
            loading={loading}
            onClick={() => handlePrompt([QA.id])}
          >
            <FireOutlined />
          </Button>
        </Tooltip>
      </Access>
      <Access accessible={access.productQAEdit && QA.permit}>
        <Tooltip title="保存">
          <Button
            style={iconButtonStyle}
            type="text"
            loading={loading}
            onClick={handleQASave}
          >
            <SaveOutlined />
          </Button>
        </Tooltip>
      </Access>
    </Space>;
  };

export default Index;
